import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, Spinner, EmptyState, Select } from "@/components/UI";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META } from "@/lib/constants";
import { CalendarRange, GanttChartSquare } from "lucide-react";

const DAY_MS = 24 * 60 * 60 * 1000;
const ZOOM_LEVELS = { Compact: 20, Normal: 28, Lebar: 40 };
const LABEL_COL_WIDTH = 130;
const TASK_COL_WIDTH = 200;
const UNASSIGNED_COLOR = "#3B4E5E";
const GROUP_PALETTE = ["#3B82F6", "#F59E0B", "#10B981", "#EC4899", "#8B5CF6", "#F97316", "#14B8A6", "#6366F1", "#84CC16", "#06B6D4", "#D946EF", "#E11D48"];

function parseDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function daysBetween(a, b) {
  return Math.round((startOfDay(b) - startOfDay(a)) / DAY_MS);
}

function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export default function Timeline() {
  const { user, isAdmin } = useAuth();
  const [projects, setProjects] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [myTaskProjectIds, setMyTaskProjectIds] = useState(new Set());
  const [projectFilter, setProjectFilter] = useState("");
  const [zoom, setZoom] = useState("Normal");
  const dayWidth = ZOOM_LEVELS[zoom];

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => setTasks([]));
    if (user?.id) {
      api.get("/tasks", { params: { assignee_id: user.id } })
        .then((r) => {
          const myTasks = r.data || [];
          setMyTaskProjectIds(new Set(myTasks.filter((t) => t.project_id).map((t) => t.project_id)));
        })
        .catch(() => {});
    }
  }, [user?.id, isAdmin]);

  const myId = String(user?.id ?? "");

  const groups = useMemo(() => {
    if (!projects || !tasks) return [];

    const visibleProjectIds = new Set();
    if (projectFilter) {
      visibleProjectIds.add(Number(projectFilter));
    } else if (isAdmin) {
      projects.forEach((p) => {
        if (p.status !== "planned") visibleProjectIds.add(p.id);
      });
    } else {
      myTaskProjectIds.forEach((id) => visibleProjectIds.add(id));
    }

    const visibleTasks = tasks.filter((t) => {
      if (["cancelled", "replaced", "planned"].includes(t.status)) return false;
      if (!t.project_id || !t.phase_id) return false;
      if (!t.start_date && !t.deadline) return false;
      if (!visibleProjectIds.has(Number(t.project_id))) return false;
      if (!isAdmin && String(t.assignee_id ?? "") !== myId) return false;
      return true;
    });

    const phaseMap = {};
    visibleTasks.forEach((t) => {
      const p = t.project;
      if (!p) return;
      const key = `${t.project_id}-${t.phase_id}`;
      if (!phaseMap[key]) {
        phaseMap[key] = {
          id: t.phase_id,
          name: t.phase?.name || "Tahapan",
          project_id: t.project_id,
          project: p,
          start: null,
          end: null,
          taskCount: 0,
          weight: 0,
          assignees: [],
        };
      }
      const s = parseDate(t.start_date);
      const e = parseDate(t.deadline);
      const entry = phaseMap[key];
      entry.taskCount += 1;
      entry.weight += Number(t.weight) || 0;
      if (t.assignee && !entry.assignees.some((a) => a.id === t.assignee.id)) entry.assignees.push(t.assignee);
      if (s && (!entry.start || s < entry.start)) entry.start = s;
      if (e && (!entry.end || e > entry.end)) entry.end = e;
    });

    const projectGroups = projects
      .filter((p) => visibleProjectIds.has(p.id))
      .map((p) => {
        const phases = Object.values(phaseMap)
          .filter((ph) => ph.project_id === p.id)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return {
          id: p.id,
          name: p.name,
          color: p.color || GROUP_PALETTE[p.id % GROUP_PALETTE.length],
          logo_url: p.logo_url,
          phases,
        };
      })
      .filter((g) => g.phases.length > 0);

    // Task tanpa project (Internal & Konsep) — tampil sebagai group virtual sendiri,
    // tiap task jadi satu bar sesuai tanggal mulai/deadline-nya
    const looseTasks = tasks.filter((t) => {
      if (["cancelled", "replaced", "planned"].includes(t.status)) return false;
      if (t.project_id) return false;
      if (!t.start_date && !t.deadline) return false;
      if (!isAdmin && String(t.assignee_id ?? "") !== myId) return false;
      return true;
    });
    const toRow = (t) => ({
      id: `task-${t.id}`,
      name: t.title,
      start: parseDate(t.start_date),
      end: parseDate(t.deadline),
      taskCount: 1,
      weight: Number(t.weight) || 0,
      assignees: t.assignee ? [t.assignee] : [],
    });
    const sortRows = (rows) => rows.sort((a, b) => ((a.start || a.end)?.getTime() || 0) - ((b.start || b.end)?.getTime() || 0));
    const looseGroups = [];
    if (!projectFilter) {
      const internal = sortRows(looseTasks.filter((t) => (t.type || "internal") !== "konsep").map(toRow));
      const konsep = sortRows(looseTasks.filter((t) => t.type === "konsep").map(toRow));
      if (internal.length) looseGroups.push({ id: "internal", name: "Internal", color: "#F59E0B", phases: internal });
      if (konsep.length) looseGroups.push({ id: "konsep", name: "Konsep", color: "#8B5CF6", phases: konsep });
    }

    return [...projectGroups, ...looseGroups];
  }, [projects, tasks, projectFilter, myTaskProjectIds, myId, isAdmin]);

  const allVisiblePhases = useMemo(() => groups.flatMap((g) => g.phases), [groups]);

  const { rangeStart, rangeEnd } = useMemo(() => {
    const dates = [];
    allVisiblePhases.forEach((ph) => {
      if (ph.start) dates.push(ph.start);
      if (ph.end) dates.push(ph.end);
    });
    const today = new Date();
    if (dates.length === 0) return { rangeStart: addDays(today, -3), rangeEnd: addDays(today, 21) };
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    return { rangeStart: addDays(min, -2), rangeEnd: addDays(max, 3) };
  }, [allVisiblePhases]);

  const totalDays = Math.max(1, daysBetween(rangeStart, rangeEnd) + 1);
  const today = startOfDay(new Date());

  const days = useMemo(() => {
    const list = [];
    for (let i = 0; i < totalDays; i++) list.push(addDays(rangeStart, i));
    return list;
  }, [rangeStart, totalDays]);

  const months = useMemo(() => {
    const list = [];
    let cursor = 0;
    while (cursor < days.length) {
      const d = days[cursor];
      const label = d.toLocaleDateString("id-ID", { month: "short", year: "numeric" });
      let count = 0;
      while (cursor < days.length && days[cursor].toLocaleDateString("id-ID", { month: "short", year: "numeric" }) === label) {
        cursor++;
        count++;
      }
      list.push({ label, count });
    }
    return list;
  }, [days]);

  if (!projects || !tasks) return <Spinner />;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="timeline-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Timeline</h1>
          <p className="text-sm text-slate-400">Kalender jadwal seluruh tahapan project berdasarkan tanggal mulai & deadline task di dalamnya.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            className="w-56"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            data-testid="timeline-project-filter"
          >
            <option value="">Semua Project</option>
            {projects
              .filter((p) => (isAdmin ? p.status !== "planned" : myTaskProjectIds.has(p.id)))
              .map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
          </Select>
          <div className="flex overflow-hidden rounded-md border border-white/10">
            {Object.keys(ZOOM_LEVELS).map((z) => (
              <button
                key={z}
                onClick={() => setZoom(z)}
                className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  zoom === z ? "bg-[#3098C6] text-white" : "bg-[#0D2842] text-slate-400 hover:text-slate-200"
                }`}
                data-testid={`timeline-zoom-${z.toLowerCase()}`}
              >
                {z}
              </button>
            ))}
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <EmptyState icon={GanttChartSquare} title="Belum ada tahapan" subtitle="Tambahkan task dengan tanggal mulai atau deadline pada tahapan untuk melihat timeline." />
      ) : (
        <Card className="p-0" data-testid="timeline-gantt-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-5 py-3">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <CalendarRange size={14} />
              {rangeStart.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}
              {" — "}
              {rangeEnd.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
              {groups.map((g) => (
                <span key={g.id} className="flex items-center gap-1.5">
                  <span className="h-2.5 w-4 rounded-sm" style={{ backgroundColor: g.color }} /> {g.name}
                </span>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <CalendarGrid groups={groups} days={days} months={months} today={today} dayWidth={dayWidth} rangeStart={rangeStart} />
          </div>
        </Card>
      )}
    </div>
  );
}

function readableTextColor(hex) {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return "#F8FAFC";
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#0F172A" : "#F8FAFC";
}

function CalendarGrid({ groups, days, months, today, dayWidth, rangeStart }) {
  return (
    <table className="border-collapse text-sm" style={{ tableLayout: "fixed" }}>
      <thead>
        <tr>
          <th
            rowSpan={2}
            className="sticky left-0 z-30 bg-[#0F172A] px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-400"
            style={{ width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH }}
          >
            Project
          </th>
          <th
            rowSpan={2}
            className="sticky z-30 bg-[#0F172A] px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-400"
            style={{ left: LABEL_COL_WIDTH, width: TASK_COL_WIDTH, minWidth: TASK_COL_WIDTH }}
          >
            Tahapan
          </th>
          {months.map((m, idx) => (
            <th key={idx} colSpan={m.count} className="border-l border-dashed border-white/15 py-1 text-center text-[11px] font-semibold text-slate-400">
              {m.label}
            </th>
          ))}
        </tr>
        <tr>
          {days.map((d, idx) => {
            const isToday = daysBetween(today, d) === 0;
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            return (
              <th
                key={idx}
                className={`border-l border-dashed border-white/15 text-center text-[10px] font-normal ${isToday ? "text-[#E0B70B]" : isWeekend ? "bg-rose-500/10 font-semibold text-rose-400" : "text-slate-500"}`}
                style={{ width: dayWidth, minWidth: dayWidth }}
              >
                {d.getDate()}
                {isToday && <div className="mx-auto mt-0.5 h-1 w-1 rounded-full bg-[#E0B70B]" />}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {groups.map((group) =>
          group.phases.map((phase, i) => (
            <tr key={`${group.id}-${phase.id}`} data-testid={`timeline-gantt-row-${group.id}-${phase.id}`}>
              {i === 0 && (
                <td
                  rowSpan={group.phases.length}
                  className="sticky left-0 z-20 px-2 py-2 text-center align-middle text-xs font-bold text-white"
                  style={{ width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH, backgroundColor: group.color }}
                >
                  {group.name}
                </td>
              )}
              <td
                className="sticky z-20 bg-[#0F172A] px-2 py-1.5 text-xs text-slate-200"
                style={{ left: LABEL_COL_WIDTH, width: TASK_COL_WIDTH, minWidth: TASK_COL_WIDTH }}
                title={phase.name}
              >
                <div className="truncate font-medium">{phase.name}</div>
              </td>
              {days.map((d, idx) => {
                const start = phase.start || phase.end;
                const end = phase.end || phase.start;
                const startIdx = start ? Math.max(0, daysBetween(rangeStart, start)) : -1;
                const endIdx = end ? Math.min(days.length - 1, daysBetween(rangeStart, end)) : -1;
                const isStartCell = startIdx >= 0 && idx === startIdx;
                const span = isStartCell ? Math.max(1, endIdx - startIdx + 1) : 0;
                const textColor = readableTextColor(group.color);
                const isToday = daysBetween(today, d) === 0;
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <td
                    key={idx}
                    className={`relative border-l border-dashed ${isToday ? "border-[#E0B70B]/40" : isWeekend ? "border-rose-500/25 bg-rose-500/[0.05]" : "border-white/15"}`}
                    style={{ width: dayWidth, minWidth: dayWidth, height: 44 }}
                  >
                    {isStartCell && (
                      <div
                        className="absolute flex items-center gap-1.5 overflow-hidden rounded-full px-2"
                        style={{
                          top: 4,
                          left: 2,
                          height: 32,
                          width: Math.max(24, span * dayWidth - 4),
                          backgroundColor: group.color,
                          zIndex: 10,
                          boxShadow: "0 1px 4px rgba(0,0,0,.35)",
                          whiteSpace: "nowrap",
                        }}
                        title={`${phase.name} · ${group.name} · ${phase.weight || phase.taskCount} pts`}
                        data-testid={`timeline-phase-bar-${phase.id}`}
                      >
                        {phase.assignees?.length ? (
                          <div className="flex shrink-0 -space-x-1.5">
                            {phase.assignees.slice(0, 3).map((a, i) =>
                              a.avatar_url ? (
                                <img
                                  key={a.id ?? i}
                                  src={a.avatar_url}
                                  alt={a.name}
                                  title={a.name}
                                  className="h-6 w-6 rounded-full object-cover ring-1 ring-[#0F172A]"
                                />
                              ) : (
                                <span
                                  key={a?.id ?? i}
                                  title={a?.name}
                                  className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ring-1 ring-[#0F172A]"
                                  style={{ backgroundColor: textColor === "#F8FAFC" ? "rgba(255,255,255,.25)" : "rgba(15,23,42,.15)", color: textColor }}
                                >
                                  {a?.name?.charAt(0)?.toUpperCase() || "?"}
                                </span>
                              )
                            )}
                            {phase.assignees.length > 3 && (
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-700 text-[9px] font-bold text-white ring-1 ring-[#0F172A]">
                                +{phase.assignees.length - 3}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                            style={{ backgroundColor: textColor === "#F8FAFC" ? "rgba(255,255,255,.25)" : "rgba(15,23,42,.15)", color: textColor }}
                          >
                            {group.name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        )}
                        <span className="ml-auto text-[11px] font-bold" style={{ color: textColor }}>
                          {phase.weight || phase.taskCount}
                        </span>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
